import React, { FC, useContext, useState, Fragment } from 'react';
import { Reorder, useDragControls, useMotionValue, motion } from 'framer-motion';
import { useRaisedShadow } from '../hooks/useRaisedShadows';
import { CHAT_TAB_VIEWS, Response } from '../types';
import { GMAMarkdown } from './GMAMarkdown';
import { useToast } from '../components/ui/toast/UseToast';
import { ToastAction } from '../components/ui/toast/Toast';
import { ChatbotContext } from '../contexts/ChatbotContext';
import { Tooltip, TooltipProvider, TooltipTrigger } from '../components/ui/Tooltip';
import { TooltipContent } from '@radix-ui/react-tooltip';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { Dialog, Transition } from '@headlessui/react';

interface StoryBuilderCardProps {
  storyResponse: Response;
  deleteStoryResponse: (genArcPromptAnalyticsUId: string) => void;
}

export const StoryBuilderCard: FC<StoryBuilderCardProps> = ({ storyResponse, deleteStoryResponse }) => {
  const dragControls = useDragControls(); // Used to define an element other than the Reorder.Item as the drag trigger
  // Both hooks below are used to add a drop shadow to the card when it is being dragged
  const y = useMotionValue(0);
  const boxShadow = useRaisedShadow(y);
  const { toast } = useToast();
  const { setActiveChatTab, isChatHistoryActive } = useContext(ChatbotContext);
  const [DeleteModal, setDeleteModal] = useState<boolean>(false);

  const removeToastNotify = () => {
    toast({
      status: 'success',
      description: isChatHistoryActive ? 'Storybuilder has been deleted' : 'Removed from Storybuilder',
    });
  };

  return (
    <>
      <Reorder.Item
        className="story-card flex flex-col w-full z-[1] rounded-lg shadow-md"
        value={storyResponse}
        dragListener={false}
        dragControls={dragControls}
        style={{ boxShadow: '0 2px 12px 0px rgba(0, 0, 0, 0.1)' }}
      >
        <motion.div
          className="drag-drop-btn flex items-center justify-center min-w-[32px] h-auto rounded-l-lg hover:cursor-grab py-4 gap-2"
          onPointerDown={(e) => dragControls.start(e)}
        >
          <img className="w-4 h-4 prevent-select" src="/icons/dragDrop.svg" alt="Drag and drop" />
        </motion.div>
        <motion.div className="h-px bg-[#707070] opacity-10"></motion.div>
        <motion.div className="story-card-text-wrapper flex flex-col text-xs leading-[19px] tracking-[-0.18px] text-black text-left pr-2 py-4 gap-1 font-light font-graphik ml-2.5 w-[97%]">
          <motion.span className="response-text prevent-select">
            <GMAMarkdown markdown={storyResponse.response} />
          </motion.span>
        </motion.div>
        <motion.div className="h-px bg-[#707070] opacity-10"></motion.div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <motion.button
                type="button"
                className="delete-btn w-full flex items-center justify-center h-auto rounded-r-lg hover:bg-gamma-bg-primary hover:bg-opacity-30 py-4"
                onClick={() => {
                  setDeleteModal(true);
                }}
              >
                <img className="w-auto h-4 prevent-select" src="/icons/trash.svg" alt="Delete" />
              </motion.button>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              className="bg-[#505050] border-0 max-w-[218px] drop-shadow-[0_12px_16px_rgba(0,0,0,0.12)] p-2"
              sideOffset={5}
            >
              <p className="tooltip-content text-white text-base capitalize not-italic">Remove</p>
              <TooltipPrimitive.Arrow width={11} height={5} style={{ fill: '#505050' }} />
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </Reorder.Item>
      {DeleteModal && (
        <Transition.Root show={DeleteModal} as={Fragment}>
          <Dialog as="div" className="relative z-[101]" onClose={() => setDeleteModal(false)}>
            {/* Opaque overlay */}
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-70 transition-opacity" />
            </Transition.Child>

            {/* Modal panel */}
            <div className="fixed inset-0 z-10 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4"
                  enterTo="opacity-100 translate-y-0"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-4"
                >
                  <div className="flex flex-col items-center flex-shrink-0 lg:w-1/2">
                    <Dialog.Panel className="relative flex border-t-4 border-t-[#A100FF] w-[882px] h-[340px] transform flex-col bg-gamma-modal-bg font-graphik text-black transition-all">
                      <div className="flex flex-row justify-end pr-[24px] pt-[28px]">
                        <img
                          src="/icons/Close.svg"
                          alt="Send suggested prompt"
                          className="w-[16px] h-[16px] cursor-pointer"
                          onClick={() => {
                            setDeleteModal(false);
                          }}
                        />
                      </div>
                      <div className="relative flex transform flex-col gap-[24px] bg-[var(--Surface-card-bg, #FFFFFF)] shadow-md pt-[56px] pb-[96px]">
                        <Dialog.Title className="text-black font-graphik text-[24px] font-normal flex justify-center items-center gap-8">
                          <img className="h-14 w-14" src="./icons/icon-caution.svg" alt="Caution Image" />
                          <span>Are you sure you want to delete it permanently ?</span>
                        </Dialog.Title>
                      </div>
                      <div className="flex flex-row flex-nowrap justify-end gap-5 pr-[32px] pt-[24px] text-sm">
                        <button
                          className="rounded-sm border border-[#000] bg-[#FFFFFF] px-8 py-[6px] hover:text-[#460073] hover:border-[#460073]"
                          onClick={() => {
                            setDeleteModal(false);
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          className="rounded-sm bg-[#000000] px-8 py-[6px] text-white hover:bg-[#460073]"
                          onClick={() => {
                            deleteStoryResponse(storyResponse.genArcPromptAnalyticsUId);
                            removeToastNotify();
                            setDeleteModal(false);
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </Dialog.Panel>
                  </div>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition.Root>
      )}
    </>
  );
};
